import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useBulkImportBatch,
  type BulkImportRecord,
  type BulkImportRowPreview,
} from "@/features/bulk-imports";
import { BulkImportStatusBadge } from "./BulkImportStatusBadge";

interface BulkImportDetailProps {
  batchId: string | null;
  onBatchUpdated: () => void;
}

export function BulkImportDetail({
  batchId,
  onBatchUpdated,
}: BulkImportDetailProps) {
  const {
    batch,
    commitResult,
    isLoading,
    isCommitting,
    error,
    commitError,
    refetch,
    commit,
  } = useBulkImportBatch(batchId);

  const summary = batch?.batch;
  const canCommitAll =
    summary?.status === "validated" && summary.errorRowCount === 0;
  const canCommitValidOnly =
    summary?.status === "validated" &&
    summary.validRowCount > 0 &&
    summary.errorRowCount > 0;

  const handleCommit = async (commitOnlyValidRows = false) => {
    try {
      await commit(commitOnlyValidRows ? { commitOnlyValidRows: true } : {});
      refetch();
      onBatchUpdated();
    } catch {
      // Hook exposes commitError.
    }
  };

  if (!batchId) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Detalle</CardTitle>
          <CardDescription>Selecciona un lote del historial.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            Sin lote seleccionado.
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Detalle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando lote
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !batch || !summary) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Detalle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error?.message ?? "No se pudo cargar el lote."}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <CardTitle className="text-lg">{summary.sourceFilename}</CardTitle>
            <CardDescription>
              {formatImportType(summary.importType)} · Subido{" "}
              {formatDateTime(summary.uploadedAt)}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={refetch}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Actualizar
            </Button>
            <Button
              size="sm"
              disabled={!canCommitAll || isCommitting}
              onClick={() => handleCommit(false)}
            >
              {isCommitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Commit
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Commit lote
                </>
              )}
            </Button>
            {canCommitValidOnly && (
              <Button
                variant="outline"
                size="sm"
                disabled={isCommitting}
                onClick={() => handleCommit(true)}
              >
                Commit validas
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <DetailMetric
            label="Estado"
            value={<BulkImportStatusBadge status={summary.status} />}
          />
          <DetailMetric label="Filas" value={summary.rowCount} />
          <DetailMetric label="Validas" value={summary.validRowCount} />
          <DetailMetric
            label="Errores"
            value={summary.errorRowCount}
            tone={summary.errorRowCount > 0 ? "danger" : undefined}
          />
          <DetailMetric label="Omitidas" value={summary.skippedRowCount} />
          <DetailMetric label="Commit" value={summary.committedRowCount} />
        </div>

        {summary.errorRowCount > 0 && summary.status === "validated" && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              El commit completo queda deshabilitado hasta corregir las filas
              invalidas. Puedes commitear solo validas con registro de filas
              omitidas.
            </span>
          </div>
        )}

        {commitResult && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            Commit registrado: {commitResult.committedRowCount} filas,{" "}
            {commitResult.skippedRowCount} omitidas,{" "}
            {commitResult.failedRowCount} fallidas.
          </div>
        )}

        {commitError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {commitError.message}
          </div>
        )}

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[1080px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-muted-foreground">
                <th className="px-3 py-2 font-medium">Fila</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium">Grupo</th>
                <th className="px-3 py-2 font-medium">Normalizado</th>
                <th className="px-3 py-2 font-medium">Errores</th>
                <th className="px-3 py-2 font-medium">Targets</th>
              </tr>
            </thead>
            <tbody>
              {batch.rows.map((row) => (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="px-3 py-3 font-mono text-xs">
                    {row.sourceRowNumber}
                  </td>
                  <td className="px-3 py-3">
                    <BulkImportStatusBadge status={row.status} />
                  </td>
                  <td className="px-3 py-3 text-muted-foreground">
                    {row.groupKey ?? "-"}
                  </td>
                  <td className="px-3 py-3">
                    <RecordPreview record={row.normalizedData} />
                  </td>
                  <td className="px-3 py-3">
                    <ValidationMessages row={row} />
                  </td>
                  <td className="px-3 py-3">
                    <TargetReferences row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone?: "danger";
}) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div
        className={
          tone === "danger"
            ? "mt-2 text-xl font-semibold text-destructive"
            : "mt-2 text-xl font-semibold"
        }
      >
        {value}
      </div>
    </div>
  );
}

function RecordPreview({ record }: { record: BulkImportRecord | null }) {
  if (!record) {
    return <span className="text-muted-foreground">-</span>;
  }

  const entries = Object.entries(record)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    )
    .slice(0, 6);

  if (entries.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <dl className="max-w-[360px] space-y-1">
      {entries.map(([key, value]) => (
        <div key={key} className="grid grid-cols-[120px_1fr] gap-2">
          <dt className="truncate text-xs text-muted-foreground">{key}</dt>
          <dd className="truncate text-xs">{formatValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function ValidationMessages({ row }: { row: BulkImportRowPreview }) {
  const errors = [
    ...(row.validationResult?.errors ?? []),
    ...(row.commitResult?.errors ?? []),
  ];

  if (errors.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <ul className="max-w-[300px] space-y-1 text-xs text-destructive">
      {errors.slice(0, 4).map((error, index) => (
        <li key={`${error.code}-${index}`} className="leading-5">
          {error.field ? `${error.field}: ` : ""}
          {error.message}
        </li>
      ))}
      {errors.length > 4 && <li>+{errors.length - 4} mas</li>}
    </ul>
  );
}

function TargetReferences({ row }: { row: BulkImportRowPreview }) {
  type TargetReference = { label: string; value: string; href?: string };
  const contactId = row.targetContactId ?? row.commitResult?.targetContactId;
  const journalEntryId =
    row.targetJournalEntryId ?? row.commitResult?.targetJournalEntryId;
  const setupMappingId =
    row.targetSetupMappingId ?? row.commitResult?.targetSetupMappingId;
  const targets = [
    contactId ? { label: "Contacto", value: contactId } : null,
    journalEntryId
      ? {
          label: "Asiento",
          value: journalEntryId,
          href: `/contabilidad/diario/${journalEntryId}`,
        }
      : null,
    setupMappingId ? { label: "Setup", value: setupMappingId } : null,
  ].filter((target): target is TargetReference => Boolean(target));

  if (targets.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <div className="max-w-[280px] space-y-1">
      {targets.map((target) =>
        target.href ? (
          <Link
            key={`${target.label}-${target.value}`}
            to={target.href}
            className="flex items-center gap-2 text-xs text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            <span className="font-medium">{target.label}</span>
            <span className="truncate">{target.value}</span>
          </Link>
        ) : (
          <div
            key={`${target.label}-${target.value}`}
            className="flex items-center gap-2 text-xs"
          >
            <ExternalLink className="h-3 w-3 text-muted-foreground" />
            <span className="font-medium">{target.label}</span>
            <span className="truncate text-muted-foreground">
              {target.value}
            </span>
          </div>
        ),
      )}
    </div>
  );
}

function formatValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value);
}

function formatImportType(value: string): string {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}
